import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { List, Container, Hidden } from "@material-ui/core";
import "./NavBar.scss";
import { LoginMenu } from "../../auth/LoginMenu";
import MainNavLinks from "./MainNavLinks";
import HeaderLogo from "./HeaderLogo";
import MobileMenu from "./MobileMenu";
import { useCurrentJourneyAndProgrammeIds } from "../../hooks/useCurrentJourneyAndProgrammeIds";
import { getProgrammesByJourneyAndProgrammeIds } from "../../../utils/JourneyUtils";
import { ReactComponent as CloseIcon } from "../../../assets/img/closeIcon.svg";
import PropTypes from "prop-types";
import { CloseSignUpModal } from "../Modals/CloseSignUpModal";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { ProgressBar } from "../../molecules/ProgressBar";

const useStyles = makeStyles(() => ({
  root: {
    padding: "0 10px 0 0",
  },
  navContainerStyles: {
    padding: 0,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  linkText: {
    textDecoration: `none`,
    color: `white`,
  },
}));

const policyLinks = [
  { name: "about us", link: "about" },
  { name: "contact us", link: "contact" },
  { name: "accessibility", link: "accessibility" },
  { name: "terms & conditions", link: "terms-and-conditions" },
  { name: "privacy", link: "privacy" },
];

const menuLinks = [
  { name: "home", slug: "home", links: [{ path: "", exact: true }] },
  { name: "my path", slug: "my-path", links: [] },
  { name: "my progress", slug: "my-progress", links: [] },
  {
    name: "explore",
    slug: "explore",
    links: [
      { name: "articles", path: "explore/articles", exact: false },
      { name: "recipes", path: "explore/recipes", exact: false },
    ],
  },
];

const NavBar = props => {
  const { hideMenu, showCloseButton, programmeTitle } = props;
  const classes = useStyles();

  const [isReady, setIsReady] = useState(false);
  const [navLinks, setNavLinks] = useState([]);
  const progressBarState = useSelector(state => state.progressBar);

  let match = useRouteMatch({
    path: "/sign-up/*/:programmeSlug",
  });

  const [programmeSlug, setProgrammeSlug] = useState(match?.params?.programmeSlug);

  useEffect(() => {
    setProgrammeSlug(match?.params?.programmeSlug);
  }, [match]);

  const [currentJourneyAndProgrammeIds, currentJourneyAndProgrammeIdsReady] =
    useCurrentJourneyAndProgrammeIds();

  const [exitProgress, setExitProgress] = useState(false);

  useEffect(() => {
    if (currentJourneyAndProgrammeIdsReady) {
      const currentProgrammes = getProgrammesByJourneyAndProgrammeIds(currentJourneyAndProgrammeIds);

      let updatedMenuLinks = [...menuLinks]; // clone

      let myPathLinks = updatedMenuLinks.find(menu => menu.name === "my path");
      myPathLinks.links = [];

      currentProgrammes.forEach(programme => {
        myPathLinks.links.push({
          name: programme.name.toLowerCase(),
          path: `my-path/${programme.slug}`,
          exact: false,
        });
      });

      let myProgressMenu = updatedMenuLinks.find(menu => menu.name === "my progress");

      if (currentProgrammes.length > 1) {
        const firstProgramme = currentProgrammes[0];
        myProgressMenu.links = [
          {
            name: firstProgramme.name.toLowerCase(),
            path: `my-progress/${firstProgramme.slug}`,
            exact: false,
          },
        ];
      } else {
        myProgressMenu.links = [];
        currentProgrammes.forEach(programme => {
          if (programme.features.myProgress === true) {
            myProgressMenu.links.push({
              name: programme.name.toLowerCase(),
              path: `my-progress/${programme.slug}`,
              exact: false,
            });
          }
        });
      }
      const links = updatedMenuLinks.filter(item => item.links.length !== 0);
      setNavLinks(links);
      setIsReady(true);
    }
  }, [currentJourneyAndProgrammeIds, currentJourneyAndProgrammeIdsReady])

  const showExitProgressModal = () => {
    setExitProgress(true);
  };

  const handleModalClose = () => {
    setExitProgress(false);
  };

  return (
    <>
      <AppBar position="static" className={`header__container ${classes.root}`}>
        <Container>
          <Toolbar>
            <List
              component="nav"
              className={`main-nav__container ${classes.navContainerStyles} ${
                programmeTitle ? "main-nav__container--has-prog-title" : ""
              }`}
              role="navigation"
              aria-label="Primary Navigation"
              id="main-navigation"
            >
              <div
                className={`header__logo-container nav-left-block ${
                  hideMenu ? "header-item__full-width" : "header-item__container"
                }`}
              >
                <HeaderLogo />
              </div>
              {programmeTitle && (
                <div className="header__programme-title__container">
                  <h3 className="header__programme-title">{programmeTitle}</h3>
                  {programmeSlug && <ProgressBar steps={progressBarState.steps} />}
                </div>
              )}
              {showCloseButton && (
                <button className="header__close-button" onClick={showExitProgressModal}>
                  <div className="close-buttton__container">
                    <CloseIcon />
                    <span className="close-button__label">Close</span>
                  </div>
                </button>
              )}

              {!hideMenu && isReady && (
                <Hidden xsDown>
                  <div className="nav-center-block">
                    <MainNavLinks mainNavItems={navLinks} />
                  </div>
                </Hidden>
              )}

              {!showCloseButton && (
                <div
                  className={`header__user-image-container nav-right-block  ${
                    hideMenu ? "header-item__full-width" : "header-item__container"
                  }`}
                >
                  <LoginMenu />
                </div>
              )}

              {!hideMenu && isReady && (
                <Hidden smUp>
                  <MobileMenu navLinks={navLinks} policyLinks={policyLinks} />
                </Hidden>
              )}
            </List>
          </Toolbar>
        </Container>
      </AppBar>
      {showCloseButton && <CloseSignUpModal showModal={exitProgress} onClose={handleModalClose} />}
    </>
  );
};

NavBar.propTypes = {
  hideMenu: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  programmeTitle: PropTypes.string,
};

export default NavBar;
